import { supabase } from '@/lib/supabase';
import type { MusicData } from '@/types/music';
import { Clock, Mic2, Music, Play, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

interface SearchResultsProps {
  results: {
    artists: MusicData[];
    songs: MusicData[];
  };
  onArtistClick: (artist: string) => void;
  onSongClick: (title: string, artist: string) => void;
  onClose: () => void;
  isSearching: boolean;
}

interface SongStats {
  firstAppearanceDate: string;
  recentPlayDates: string[];
  count: number;
  totalCount: number;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  onArtistClick,
  onSongClick,
  onClose,
}) => {
  const [songStats, setSongStats] = useState<Map<string, SongStats>>(new Map());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // 노래별 재생 날짜와 횟수를 계산하는 함수
  const getSongStats = useCallback(async (songs: MusicData[]) => {
    const stats = new Map<string, SongStats>();

    // 현재 검색 결과의 통계 계산
    for (const song of songs) {
      const key = `${song.title}-${song.artist}`;
      if (!stats.has(key)) {
        stats.set(key, {
          firstAppearanceDate: song.broadcast_date,
          recentPlayDates: [],
          count: 0,
          totalCount: 0,
        });
      }
      const stat = stats.get(key);
      if (stat) {
        stat.count++;
      }
    }

    // 각 노래의 전체 재생 횟수와 날짜 조회
    for (const [key] of stats) {
      const [title, artist] = key.split('-');
      const { data, error } = await supabase
        .from('music_data')
        .select('broadcast_date')
        .eq('title', title)
        .eq('artist', artist)
        .order('broadcast_date', { ascending: false });

      if (!error && data) {
        const stat = stats.get(key);
        if (stat) {
          stat.totalCount = data.length;

          // 모든 재생 날짜를 수집하고 정렬 (이미 order로 정렬되어 있음)
          stat.recentPlayDates = [...new Set(data.map((item) => item.broadcast_date))];

          // 첫 등장 날짜 찾기 (마지막 날짜가 가장 오래된 날짜)
          stat.firstAppearanceDate = data[data.length - 1].broadcast_date;
        }
      }
    }

    setSongStats(stats);
  }, []);

  useEffect(() => {
    getSongStats(results.songs);
  }, [results.songs, getSongStats]);

  // 노래 결과를 최근 재생 날짜순으로 정렬
  const sortedSongResults = Array.from(songStats.entries()).sort((a, b) => {
    const dateA = a[1].recentPlayDates[0] ? new Date(a[1].recentPlayDates[0]).getTime() : 0;
    const dateB = b[1].recentPlayDates[0] ? new Date(b[1].recentPlayDates[0]).getTime() : 0;
    return dateB - dateA;
  });

  const handleArtistClick = (artist: string) => {
    // Call the original onArtistClick for the search functionality
    onArtistClick(artist);
    // Navigate to the artist page by redirecting
    window.location.href = `/artist/${encodeURIComponent(artist)}`;
    // Close the search results
    onClose();
  };

  const maxResults = isMobile ? 5 : 10;
  const limitedArtists = results.artists.slice(0, maxResults);
  const limitedSongs = sortedSongResults.slice(0, maxResults);

  return (
    <div className="search-results rounded-lg shadow-lg max-h-[400px] overflow-y-auto bg-black border border-gray-900 relative z-50">
      <div className="sticky top-0 z-10 bg-black sticky-header p-2 flex justify-between items-center border-b border-gray-900">
        <div className="text-sm font-medium text-gray-400">Search Results</div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 hover:bg-gray-900 rounded-full transition-colors"
        >
          <X size={16} className="text-gray-400" />
        </button>
      </div>

      {limitedArtists.length > 0 && (
        <div className="p-4 border-b border-gray-900">
          <div className="flex items-center gap-2 mb-2">
            <Mic2 size={16} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-400">Artists</h3>
            {results.artists.length > maxResults && (
              <span className="text-xs text-gray-500">
                (showing {maxResults} of {results.artists.length})
              </span>
            )}
          </div>
          <div className="space-y-2">
            {limitedArtists.map((item) => {
              const handleClick = () => handleArtistClick(item.artist);
              const handleKeyDown = (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              };
              return (
                <button
                  key={`artist-${item.artist}`}
                  type="button"
                  className="result-item p-2 rounded cursor-pointer transition-colors hover:bg-gray-900 w-full text-left"
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                >
                  <div className="text-sm font-medium text-gray-200">{item.artist}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {limitedSongs.length > 0 && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Music size={16} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-400">Songs</h3>
            {sortedSongResults.length > maxResults && (
              <span className="text-xs text-gray-500">
                (showing {maxResults} of {sortedSongResults.length})
              </span>
            )}
          </div>
          <div className="space-y-2">
            {limitedSongs.map(([key, stat]) => {
              const [title, artist] = key.split('-');
              const handleClick = () => onSongClick(title, artist);
              const handleKeyDown = (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              };
              return (
                <button
                  key={`song-${key}`}
                  type="button"
                  className="result-item p-2 rounded cursor-pointer transition-colors hover:bg-gray-900 w-full text-left"
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-200 text-ellipsis overflow-hidden">
                      {title}
                    </div>
                    <div className="play-count flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-900 ml-2 flex-shrink-0">
                      <Play size={12} />
                      <span className="text-xs font-medium">{stat.totalCount}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 text-ellipsis overflow-hidden">
                    {artist}
                  </div>
                  {!isMobile && (
                    <div className="mt-1 text-xs text-gray-500">
                      <div>First appeared: {stat.firstAppearanceDate}</div>
                      {stat.recentPlayDates.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Clock size={12} className="text-gray-400" />
                          <span>
                            Recent plays: {stat.recentPlayDates.slice(0, 3).join(', ')}
                            {stat.recentPlayDates.length > 3 &&
                              ` (${stat.recentPlayDates.length - 3} more)`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {results.artists.length === 0 && results.songs.length === 0 && (
        <div className="p-4 text-center text-gray-400">No results found</div>
      )}
    </div>
  );
};

export default SearchResults;
